import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { SquareImageUpload } from '@/components/molecules/square-image-upload.molecule'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { UserProfileSectionProps } from './user-profile.interface'
import { type UserProfileFormSchemaType, userProfileFormSchema } from './user-profile.schema'

const UserProfileSectionBase: React.FC<UserProfileSectionProps> = ({
  firstName,
  lastName,
  imageUrl,
  onNameChange,
  onAvatarChange,
  onAvatarRemove,
  isLoading = false,
  className,
}): ReactNode => {
  const form = useForm<UserProfileFormSchemaType>({
    resolver: zodResolver(userProfileFormSchema),
    defaultValues: { firstName, lastName },
  })

  useEffect(() => {
    form.reset({ firstName, lastName })
  }, [firstName, lastName, form])

  const displayName = useMemo(() => `${firstName} ${lastName}`.trim(), [firstName, lastName])

  const handleSubmit = (data: UserProfileFormSchemaType): void => {
    onNameChange(data.firstName, data.lastName)
  }

  return (
    <section className={cn('user-profile-section', className)}>
      <UserProfileHeader />
      <UserProfileFormContent
        form={form}
        imageUrl={imageUrl}
        displayName={displayName}
        isLoading={isLoading}
        onAvatarChange={onAvatarChange}
        onAvatarRemove={onAvatarRemove}
        onSubmit={handleSubmit}
      />
    </section>
  )
}

const UserProfileHeader: React.FC = () => (
  <div className="user-profile-header mb-4">
    <h3 className="text-lg font-medium">Personal Profile</h3>
    <p className="text-sm text-muted-foreground">Manage your personal avatar and display name.</p>
  </div>
)

interface UserProfileFormContentProps {
  form: ReturnType<typeof useForm<UserProfileFormSchemaType>>
  imageUrl: string | null
  displayName: string
  isLoading: boolean
  onAvatarChange: (file: File | null) => void
  onAvatarRemove: () => void
  onSubmit: (data: UserProfileFormSchemaType) => void
}

const UserProfileFormContent: React.FC<UserProfileFormContentProps> = ({
  form,
  imageUrl,
  displayName,
  isLoading,
  onAvatarChange,
  onAvatarRemove,
  onSubmit,
}) => {
  const { isDirty, isValid } = form.formState

  return (
    <div className="user-profile-form-wrapper flex flex-col gap-6 sm:flex-row sm:items-start">
      <div className="user-profile-avatar-wrapper shrink-0">
        <SquareImageUpload
          imageUrl={imageUrl}
          fallbackText={displayName}
          onImageChange={onAvatarChange}
          onImageRemove={onAvatarRemove}
          isDisabled={isLoading}
          size="lg"
          uploadTooltip="Upload profile picture"
          changeTooltip="Change profile picture"
          removeTooltip="Remove profile picture"
        />
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="user-profile-name-form flex-1 space-y-4"
        >
          <UserNameFormFields control={form.control} isLoading={isLoading} />
          {isDirty && <UserProfileSaveButton isValid={isValid} isLoading={isLoading} />}
        </form>
      </Form>
    </div>
  )
}

interface UserNameFormFieldsProps {
  control: ReturnType<typeof useForm<UserProfileFormSchemaType>>['control']
  isLoading: boolean
}

const UserNameFormFields: React.FC<UserNameFormFieldsProps> = ({ control, isLoading }) => (
  <div className="user-profile-name-fields grid gap-4 sm:grid-cols-2">
    <FormField
      control={control}
      name="firstName"
      render={({ field }) => (
        <FormItem>
          <FormLabel>First Name</FormLabel>
          <FormControl>
            <Input
              {...field}
              placeholder="Enter first name"
              disabled={isLoading}
              className="user-profile-firstname-input"
            />
          </FormControl>
          <FormDescription>Your first name as it appears on your profile.</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
    <FormField
      control={control}
      name="lastName"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Last Name</FormLabel>
          <FormControl>
            <Input
              {...field}
              placeholder="Enter last name"
              disabled={isLoading}
              className="user-profile-lastname-input"
            />
          </FormControl>
          <FormDescription>Your last name as it appears on your profile.</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  </div>
)

interface UserProfileSaveButtonProps {
  isValid: boolean
  isLoading: boolean
}

const UserProfileSaveButton: React.FC<UserProfileSaveButtonProps> = ({ isValid, isLoading }) => (
  <Button type="submit" disabled={!isValid || isLoading} className="user-profile-save-button">
    {isLoading ? (
      <>
        <Loader2Icon className="mr-2 size-4 animate-spin" />
        Saving...
      </>
    ) : (
      'Save Changes'
    )}
  </Button>
)

export const UserProfileSection = memo(UserProfileSectionBase)

UserProfileSection.displayName = 'UserProfileSection'
